/* ==========================================================================
   SelectRxjs > Client > Prototype
   ========================================================================== */

/* Modules */
const { Store } = require('@bluekiri/alexandria/helpers/state');
const Choices = require('choices.js');
const { fromEvent, of } = require('rxjs');
const { switchMap, filter } = require('rxjs/operators');

const { actions, types } = require('./store/actions');
const { reducer } = require('./store/reducer');

const { initLoader, destroyLoader } = require('./loader');

const Utils = require('./utils');

const configuration = require('./config');

/* Helpers */
const setDataMultiple = instance => {
    const data = instance.choices.getValue();

    if (data.length > 0) {
        instance.actions.selectOption(data.map(item => ({ value: item.value, label: item.label })));

        if (!instance.element.classList.contains(configuration().css.hasItems)) {
            instance.element.classList.add(configuration().css.hasItems);
        }
    }
};

const setDataSingle = instance => {
    const data = instance.choices.getValue();

    if (data) {
        instance.actions.selectOption({ value: data.value, label: data.label });
    }
};

/* Prototype */
const prototype = {
    /* Pairing label */
    setPairing() {
        if (this.hooks.label) {
            this.hooks.select.setAttribute('id', this.id);
            this.hooks.label.setAttribute('for', this.id);
        }

        return this;
    },
    /* Setup choices */
    setupChoices() {
        if (this.choices) return this;

        /* Data */
        const { multiple } = this.config;

        /* Literals */
        const {
            noResultsText = 'No results found',
            searchPlaceholderValue = 'Type to search...'
        } = this.config.literals;

        /* Create 3rd party library choices instance */
        this.choices = new Choices(this.hooks.select, {
            searchEnabled: true,
            renderChoiceLimit: -1 /* limit the quantity of options shown, -1 infinite */,
            searchResultLimit: 5 /* Limit the quantity of options to show on seach, -1 infinite */,
            removeItemButton: multiple /* for multiple select true */,
            placeholderValue: searchPlaceholderValue,
            searchPlaceholderValue,
            loadingText: 'Loading...',
            noResultsText,
            noChoicesText: 'No choices to choose from',
            itemSelectText: 'Press to select',
            sortFn: () => {},
            ...this.config.choices
        });

        return this;
    },
    /* Set up store */
    setupStore() {
        if (this.store) return this;

        this.store = new Store({
            select: reducer
        });

        return this;
    },
    /* Set up actions */
    setupActions() {
        if (this.actions) return this;

        this.actions = {
            destroy: () => this.store.dispatch(actions.destroy()),
            display: () => this.store.dispatch(actions.display()),
            hide: () => this.store.dispatch(actions.hide()),
            resetStatus: () => this.store.dispatch(actions.resetStatus()),
            setError: () => this.store.dispatch(actions.setError()),
            setValid: () => this.store.dispatch(actions.setValid()),
            setOption: value => this.store.dispatch(actions.setOption(value)),
            setOptions: options => this.store.dispatch(actions.setOptions(options)),
            selectOption: value => this.store.dispatch(actions.selectOption(value)),
            enable: () => this.store.dispatch(actions.enable()),
            disable: () => this.store.dispatch(actions.disable())
        };

        return this;
    },
    /* Set up reactions */
    setupReactions() {
        if (this.reactions) return this;

        this.reactions = {
            destroy: this.store.subscribe((state, action) => {
                if (action.type !== types.DESTROY) return;

                /* Loader */
                destroyLoader(this);

                /* Clean subscriptions */
                this.unsubscribe();
                [...Object.keys(this.reactions)].forEach(key => this.reactions[key].unsubscribe());
                this.choices.destroy();

                /* Remove from DOM */
                this.element.parentNode.removeChild(this.element);

                /* Clean instance */
                [...Object.keys(this)].forEach(key => delete this[key]);
            }),
            display: this.store.subscribe((state, action) => {
                if (action.type !== types.DISPLAY) return;

                const { css } = this.config;

                this.element.classList.remove(css.hidden);
            }),
            hide: this.store.subscribe((state, action) => {
                if (action.type !== types.HIDE) return;

                const { css } = this.config;

                this.element.classList.add(css.hidden);
            }),
            resetStatus: this.store.subscribe((state, action) => {
                if (action.type !== types.RESET_STATUS) return;

                const { css } = this.config;

                this.element.classList.remove(css.valid);
                this.element.classList.remove(css.error);
            }),
            setError: this.store.subscribe((state, action) => {
                if (action.type !== types.SET_ERROR) return;

                const { css } = this.config;

                this.element.classList.remove(css.valid);
                this.element.classList.add(css.error);
            }),
            setValid: this.store.subscribe((state, action) => {
                if (action.type !== types.SET_VALID) return;

                const { css } = this.config;

                this.element.classList.remove(css.error);
                this.element.classList.add(css.valid);
            }),
            setOptions: this.store.subscribe((state, action) => {
                if (action.type !== types.SET_OPTIONS) return;

                this.choices.setChoices(action.payload, 'value', 'label', true);
            }),
            setOption: this.store.subscribe((state, action) => {
                if (action.type !== types.SET_OPTION) return;

                /* Data */
                const { multiple } = this.config;

                /* Set that option to choices */
                this.choices.setChoiceByValue(action.payload);

                /* Update UI in case of multiple */
                if (multiple) {
                    if (this.element.classList.contains(configuration().css.hasItems)) return;

                    this.element.classList.add(configuration().css.hasItems);
                }
            }),
            enable: this.store.subscribe((state, action) => {
                if (action.type !== types.ENABLE) return;

                /* Enable choices */
                this.choices.enable();

                /* Update UI */
                this.element.classList.remove(configuration().css.disabled);
            }),
            disable: this.store.subscribe((state, action) => {
                if (action.type !== types.DISABLE) return;

                /* Disable choices */
                this.choices.disable();

                /* Update UI */
                this.element.classList.add(configuration().css.disabled);
            })
        };

        return this;
    },
    /* Subscribe */
    subscribe() {
        if (this.subscriptions) this.unsubscribe();

        this.subscriptions = {
            changes: fromEvent(this.element, 'change')
                .pipe(
                    switchMap(() => of(this.choices.getValue())),
                    filter(data => data)
                )
                .subscribe(data => {
                    /* Data */
                    const { value, label } = data;
                    const { multiple } = this.config;

                    /* Set value depending on mode */
                    if (multiple) {
                        this.actions.selectOption(data.map(item => ({ value: item.value, label: item.label })));

                        if (data.length > 0) {
                            if (this.element.classList.contains(configuration().css.hasItems)) return;

                            this.element.classList.add(configuration().css.hasItems);
                        } else {
                            this.element.classList.remove(configuration().css.hasItems);
                        }
                    } else {
                        /* Dispatch action to set value */
                        this.actions.selectOption({ value, label });
                    }
                }),
            showDropdown: fromEvent(this.element, 'showDropdown').subscribe(() => {
                /* Utils */
                const { fixSelectedChoice } = Utils(this);

                /**
                 * Fix selected issue
                 */
                fixSelectedChoice();
            })
        };

        return this;
    },
    /* Unsubscribe */
    unsubscribe() {
        if (!this.subscriptions) return this;

        [...Object.keys(this.subscriptions)].forEach(key => this.subscriptions[key].unsubscribe());

        return this;
    },
    /* Start */
    start() {
        if (!this.config.start) return this;

        const { items, value = false, multiple, disabled } = this.config;

        /* Set options meta items */
        if (!items) return this;

        /* Options */
        this.actions.setOptions(items);

        if (multiple) {
            setDataMultiple(this);
        } else {
            setDataSingle(this);
        }

        /**
         * In case we have a specified value above the default on choices
         * we have to override it so we have the flexibility to have
         * a unique item set for multiple usages with custom values on
         * each select
         */
        if (value) {
            this.actions.setOption(value);
        }

        if (disabled) {
            this.actions.disable();
        }

        return this;
    },
    /* Init */
    init() {
        /* Pairing label */
        this.setPairing();

        /* Setup choices */
        this.setupChoices();

        /* Setup store */
        this.setupStore();

        /* Setup actions */
        this.setupActions();

        /* Setup reactions */
        this.setupReactions();

        /* Subscribe */
        this.subscribe();

        /* Loader */
        initLoader(this);

        /* Start */
        this.start();
    }
};

/* Exports */
module.exports = prototype;
